// src/components/auth/login/Login.js
import React, { useState } from "react";
import { useGoogleLogin } from "@react-oauth/google";
import { useMutation } from "@apollo/client";
import axios from "axios";
import {
  Backdrop,
  CircularProgress,
  OutlinedInput,
  Typography,
  Button,
  Link,
  Box,
  Grid,
  Paper, 
  SvgIcon
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import jwtDecode from "jwt-decode";
import { LOGIN_USER, SIGNUP_USER_GOOGLE } from "../../../graphql/mutations";
import useLoadReduxStoreData from "../../../redux/reducers/loadReduxStoreData";
import { BusinessQueryNames } from "../../../graphql/queries";
import { useAuthStatus } from "../../../util/useAuthStatus";
import { useTokenLoader } from "../../../util/tokenLoader";
import { getRouteMap } from "../../../pages/business/defaultPageDialog";
import { useSelectedBusiness } from "../../../redux/store";
const GoogleIcon = (props) => (
  <SvgIcon {...props}>
    <path d="M21.35 11.1h-9.18v2.94h5.27c-.23 1.25-.95 2.31-2.03 3.02v2.5h3.29c1.92-1.76 3.03-4.36 3.03-7.46 0-.5-.04-.98-.11-1.45z" fill="#4285F4" />
    <path d="M12.17 22c2.75 0 5.06-.91 6.75-2.47l-3.29-2.5c-.91.61-2.07.97-3.46.97-2.66 0-4.91-1.8-5.72-4.21H3.74v2.65C5.43 19.8 8.07 22 12.17 22z" fill="#34A853" />
    <path d="M6.45 13.62a6.97 6.97 0 0 1 0-4.38V6.6H3.74a11.77 11.77 0 0 0 0 8.8l2.71-2.65z" fill="#FBBC05" />
    <path d="M12.17 5.36c1.49 0 2.83.51 3.88 1.51l2.91-2.91C17.23 2.23 14.92 1 12.17 1 8.07 1 5.43 3.2 3.74 6.6l2.71 2.65c.81-2.41 3.06-4.21 5.72-4.21z" fill="#EA4335" />
    <path d="M1 1h22v22H1z" fill="none" />
  </SvgIcon>
);



export default function Login({ login: onLoginSuccess }) {
  const { trigger: fetchToken } = useTokenLoader();
  const { isLoggedIn } = useAuthStatus();
  const { refetch: reloadBusinesses } = useLoadReduxStoreData(
    BusinessQueryNames.GET_MY_BUSINESSES,
    { enabled: isLoggedIn }
  );
  const selectedBusiness = useSelectedBusiness();

  const location = useLocation();
  const navigate = useNavigate();

  // Form state
  const [email, setEmail] = useState(
    new URLSearchParams(window.location.search).get("email") || ""
  );
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState([]);
  const [updating, setUpdating] = useState(false);

  // GraphQL mutations
  const [loginUser] = useMutation(LOGIN_USER);
  const [signUpWithGoogle] = useMutation(SIGNUP_USER_GOOGLE);

  /**
   * Determine where to send the user after successful login:
   * 1. React-Router state `from` (if you ever guard via <Navigate … state={{ from }}>)
   * 2. localStorage.redirectAfterLogin
   * 3. localStorage.redirectAfterBusiness
   * 4. Fallback based on whether they own a business yet
   */
  function determineDestination(decoded) {
    // 1️⃣ React Router “from” in location.state
    const fromState = location.state && location.state.from;
    if (fromState) {
      return fromState;
    } 

    // 2) Gather your slugs
    const businessUrl = selectedBusiness?.businessBasic?.url;
    const staffUrl = decoded.url;
    const bookingTypeLink = "biz/bookingtypes";
    // 3) Delegate to your shared function
    const routeMap = getRouteMap({ businessUrl, staffUrl, bookingTypeLink });

    // 4) Return whatever the user picked (falling back to 1)
    const id = decoded.defaultPageId || 1;
    const url = routeMap[id] || routeMap[1];


    // 4️⃣ final default
    return decoded.defaultBusinessId ? url : "/biz/business";
  }

  // Handle email/password login
  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors([]);

    if (!email.trim()) {
      setErrors(["Email is required"]);
      return;
    }
    if (!password.trim()) {
      setErrors(["Password is required"]);
      return;
    }

    try {
      const result = await loginUser({ variables: { email, password } });
      const token = result.data.login.token;

      // Save token and notify parent
      localStorage.setItem("token", token); 
      onLoginSuccess(token);

      const decoded = jwtDecode(token);
      fetchToken();

      // If they already have a business, reload it
      if (decoded.defaultBusinessId) {
        await reloadBusinesses();
      }

      // Redirect back to original path (or fallback)
      const dest = determineDestination(decoded);
      navigate(dest, { replace: true });
    } catch (err) {
      setErrors([err.message || "Login failed. Please try again."]);
    }
  };

  // Handle Google SSO
  const googleLogin = useGoogleLogin({
    onSuccess: async (response) => {
      setUpdating(true);
      try {
        const userInfo = await axios.get(
          `https://www.googleapis.com/oauth2/v1/userinfo?access_token=${response.access_token}`
        );
        const res = await signUpWithGoogle({
          variables: {
            email: userInfo.data.email,
            username: userInfo.data.name,
          },
        });

        const token = res.data.signUpGoogle.token;
        localStorage.setItem("token", token);
        onLoginSuccess(token); 

        const decoded = jwtDecode(token);
        fetchToken();

        // If they already have a business, reload it
        if (decoded.defaultBusinessId) {
          await reloadBusinesses();
        }

        // Redirect as above
        const dest = determineDestination(decoded);
        navigate(dest, { replace: true });
      } catch (err) {
        console.error("Google login failed:", err);
        setErrors(["Something went wrong with Google login."]);
      } finally {
        setUpdating(false);
      }
    },
    onError: (err) => {
      console.error("Google login error:", err);
      setErrors(["Google login failed. Try again."]);
    },
    prompt: "select_account",
    ux_mode: /iPad|iPhone|iPod/.test(navigator.userAgent)
      ? "redirect"
      : "popup",
  });

  return (
    <Box display="flex" justifyContent="center" alignItems="center" mt={8}>
      <Paper elevation={3} sx={{ p: 4, width: "100%", maxWidth: 400 }}>
        <Typography variant="h5" align="center" gutterBottom>
          Login to Your Account
        </Typography>

        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <OutlinedInput
                fullWidth
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <OutlinedInput
                fullWidth
                placeholder="Password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </Grid>
          </Grid>
          <Box mt={2}>
            <Link href="/forgotpassword" underline="hover">
              Forgot your password?
            </Link>
          </Box>
          <Button type="submit" variant="contained" fullWidth sx={{ mt: 3 }}>
            Login
          </Button>
        </form>

        <Button
          variant="outlined"
          fullWidth
          startIcon={<GoogleIcon />}
          onClick={googleLogin}
          sx={{ mt: 2 }}
        >
          Sign in with Google
        </Button>

        {errors.length > 0 && (
          <Box mt={2}>
            {errors.map((msg, i) => (
              <Typography key={i} color="error">
                {msg}
              </Typography>
            ))}
          </Box>
        )}

        <Typography align="center" variant="body2" sx={{ mt: 2 }}>
          Don’t have an account? <Link href="/signup">Sign Up</Link>
        </Typography>

        <Backdrop open={updating} sx={{ zIndex: 9999, color: "#fff" }}>
          <CircularProgress />
          <Typography variant="h6" sx={{ ml: 2 }}>
            Signing you in...
          </Typography>
        </Backdrop>
      </Paper>
    </Box>
  );
}
